import React, { useState, useEffect } from 'react';
import { rhuService } from '../../../../services/rhuService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  List as ListIcon
} from '@mui/icons-material';

const RuralHealthHome = ({ user }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [paidOrders, setPaidOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewingOrder, setViewingOrder] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [orNumber, setOrNumber] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    const result = await rhuService.getAllOrders();
    if (result.success) {
      // Filter pending and paid orders
      const pending = result.data.filter(order => order.status === 'pending');
      const paid = result.data.filter(order => order.status === 'paid')
        .slice(0, 10); // Show only 10 most recent paid orders
      setPendingOrders(pending);
      setPaidOrders(paid);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  const calculateAge = (birthdate) => {
    if (!birthdate) return '';
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleViewOrder = async (order) => {
    setLoading(true);
    const result = await rhuService.getOrderWithItems(order.id);
    if (result.success) {
      setViewingOrder(result.data);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  const openPaymentModal = (order) => {
    setProcessingPayment(order);
    setOrNumber('');
  };

  const handleProcessPayment = async () => {
    if (!orNumber.trim()) {
      setError('Please enter OR Number');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setLoading(true);
    // Get the current user's full name
    const processedBy = `${user.firstname} ${user.lastname}`;
    const result = await rhuService.updateOrderStatus(processingPayment.id, 'paid', orNumber, processedBy);
    
    if (result.success) {
      setSuccess(`Payment processed successfully! OR #${orNumber}`);
      setProcessingPayment(null);
      setOrNumber('');
      loadOrders();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
      setTimeout(() => setError(''), 3000);
    }
    setLoading(false);
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Welcome to Rural Health Unit, {user.firstname}! 👋
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Rural Health Unit Management System
          </Typography>
        </Box>

        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Pending Orders Section */}
        <Card 
          sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
            color: 'white',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon /> Pending Orders of Payment
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {pendingOrders.length} order{pendingOrders.length !== 1 ? 's' : ''} awaiting payment
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              color="inherit" 
              sx={{ color: 'primary.main', width: { xs: '100%', sm: 'auto' } }}
              onClick={loadOrders}
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              disabled={loading}
            >
              Refresh
            </Button>
          </CardContent>

        </Card>

        {/* Pending Orders Table */}
        <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            {pendingOrders.length === 0 && !loading ? (
              <Box sx={{ textAlign: 'center', p: 6, bgcolor: 'background.default' }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="text.primary">All Caught Up!</Typography>
                <Typography variant="body2" color="text.secondary">No pending orders of payment at the moment</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell>Order #</TableCell>
                      <TableCell>Patient Name</TableCell>
                      <TableCell>Age</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>{order.order_number}</TableCell>
                        <TableCell fontWeight="bold">{order.patient?.name || 'N/A'}</TableCell>
                        <TableCell>
                          {order.patient?.birthdate ? `${calculateAge(order.patient?.birthdate)} years` : (order.patient?.age ? `${order.patient.age} years` : 'N/A')}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {order.patient?.address || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`₱${parseFloat(order.total_amount).toFixed(2)}`} 
                            color="warning" 
                            sx={{ fontWeight: 'bold' }} 
                          />
                        </TableCell>
                        <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Button 
                              size="small" 
                              variant="outlined" 
                              color="info" 
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleViewOrder(order)}
                            >
                              View
                            </Button>
                            <Button 
                              size="small" 
                              variant="contained" 
                              color="success" 
                              startIcon={<PaymentIcon />}
                              onClick={() => openPaymentModal(order)}
                            >
                              Pay
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Recent Paid Orders Section */}
        <Card 
          sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
            color: 'white',
            borderRadius: 2
          }}
        >
          <CardContent>
            <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon /> Recent Paid Orders
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Last {paidOrders.length} completed payment{paidOrders.length !== 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>

        {/* Paid Orders Table */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            {paidOrders.length === 0 && !loading ? (
              <Box sx={{ textAlign: 'center', p: 6, bgcolor: 'background.default' }}>
                <ReceiptIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" color="text.primary">No Paid Orders Yet</Typography>
                <Typography variant="body2" color="text.secondary">Completed payments will appear here</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell>Order #</TableCell>
                      <TableCell>OR Number</TableCell>
                      <TableCell>Patient Name</TableCell>
                      <TableCell>Age</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Payment Date</TableCell>
                      <TableCell>Processed By</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paidOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>{order.order_number}</TableCell>
                        <TableCell>
                          <Chip label={order.or_number || 'N/A'} color="success" size="small" />
                        </TableCell>
                        <TableCell fontWeight="bold">{order.patient?.name || 'N/A'}</TableCell>
                        <TableCell>
                          {order.patient?.birthdate ? `${calculateAge(order.patient?.birthdate)} years` : (order.patient?.age ? `${order.patient.age} years` : 'N/A')}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`₱${parseFloat(order.total_amount).toFixed(2)}`} 
                            color="warning" 
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }} 
                          />
                        </TableCell>
                        <TableCell>
                          {order.paid_at ? new Date(order.paid_at).toLocaleDateString() + ' ' + 
                           new Date(order.paid_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
                           : 'N/A'}
                        </TableCell>
                        <TableCell>{order.processed_by || 'N/A'}</TableCell>
                        <TableCell align="center">
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="info" 
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewOrder(order)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* View Order Modal */}
        <Dialog 
          open={!!viewingOrder} 
          onClose={() => setViewingOrder(null)} 
          maxWidth="md" 
          fullWidth
        >
          {viewingOrder && (
            <>
              <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon /> Order Details - {viewingOrder.order_number}
                </Box>
                <IconButton onClick={() => setViewingOrder(null)} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={3}>
                  {/* Patient Info */}
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Typography variant="subtitle1" color="primary.main" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon /> Patient Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Name</Typography>
                          <Typography variant="body1" fontWeight="bold">{viewingOrder.patient?.name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Age</Typography>
                          <Typography variant="body1">
                            {viewingOrder.patient?.birthdate ? `${calculateAge(viewingOrder.patient?.birthdate)} years` : (viewingOrder.patient?.age ? `${viewingOrder.patient.age} years` : 'N/A')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Address</Typography>
                          <Typography variant="body1">{viewingOrder.patient?.address}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Services */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary.main" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ListIcon /> Services
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell align="center">Qty</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Subtotal</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewingOrder.items?.map(item => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">{item.service_name}</Typography>
                                {item.subcategory && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.category && `${item.category} → `}{item.subcategory}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">{item.quantity}</TableCell>
                              <TableCell align="right">₱{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>₱{parseFloat(item.subtotal).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ bgcolor: 'primary.lighter' }}>
                            <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>TOTAL:</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.1rem' }}>
                              ₱{parseFloat(viewingOrder.total_amount).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  {/* Payment Details */}
                  {viewingOrder.status === 'paid' && (
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.lighter', border: '1px solid', borderColor: 'success.light', borderRadius: 2 }}>
                        <Typography variant="subtitle1" color="success.dark" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon /> Payment Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="success.dark">OR Number</Typography>
                            <Typography variant="body1" fontWeight="bold">{viewingOrder.or_number || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="success.dark">Date</Typography>
                            <Typography variant="body1">
                              {viewingOrder.paid_at ? new Date(viewingOrder.paid_at).toLocaleDateString('en-PH') : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="success.dark">Processed By</Typography>
                            <Typography variant="body1">{viewingOrder.processed_by || 'N/A'}</Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setViewingOrder(null)}>Close</Button>
                {viewingOrder.status !== 'paid' && (
                  <Button 
                    variant="contained" 
                    color="success" 
                    startIcon={<PaymentIcon />}
                    onClick={() => {
                      openPaymentModal(viewingOrder);
                      setViewingOrder(null);
                    }}
                  >
                    Process Payment
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Process Payment Modal */}
        <Dialog 
          open={!!processingPayment} 
          onClose={() => setProcessingPayment(null)} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
            Process Payment
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {processingPayment && (
              <Stack spacing={3} sx={{ mt: 1 }}>
                <Alert severity="info" icon={<PaymentIcon />}>
                  <Typography variant="subtitle2">Payment Summary</Typography>
                  <Typography variant="body2">Order #: <strong>{processingPayment.order_number}</strong></Typography>
                  <Typography variant="body2">Patient: <strong>{processingPayment.patient?.name}</strong></Typography>
                  <Typography variant="h6" color="success.dark" sx={{ mt: 1 }}>
                    Amount to Pay: <strong>₱{parseFloat(processingPayment.total_amount).toFixed(2)}</strong>
                  </Typography>
                </Alert>

                <TextField
                  label="Official Receipt (OR) Number"
                  fullWidth
                  required
                  autoFocus
                  value={orNumber}
                  onChange={(e) => setOrNumber(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><ReceiptIcon color="action" /></InputAdornment>,
                  }}
                  helperText="Please enter the official receipt number for this payment"
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setProcessingPayment(null)}>Cancel</Button>
            <Button 
              variant="contained" 
              color="success" 
              onClick={handleProcessPayment}
              disabled={loading || !orNumber.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              Confirm Payment
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
};

export default RuralHealthHome;
